import { useCallback } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import { getLotteryInfo, LOTTERY_ANCHOR } from '../utils/luckyLottery';

dayjs.locale('ko');

export const getWeekRange = (date: dayjs.Dayjs): { start: dayjs.Dayjs; end: dayjs.Dayjs } => {
  const sunday = date.subtract(date.day(), 'day').startOf('day');
  return { start: sunday, end: sunday.add(6, 'day') };
};

export const getInitialWeekStart = (): dayjs.Dayjs => getWeekRange(dayjs()).start;

type Props = {
  startDate: string;
  endDate: string;
  onChange: (startDate: string, endDate: string) => void;
  onSearch?: () => void;
};

export default function LotteryWeekSelector({ startDate, endDate, onChange }: Props) {
  const currentMonday = dayjs(startDate);
  const currentSaturday = dayjs(endDate);

  const maxMonday = getInitialWeekStart();
  const isMaxWeek = !currentMonday.isBefore(maxMonday, 'day');
  const isMinWeek = !currentMonday.isAfter(LOTTERY_ANCHOR, 'day');

  const handlePrev = useCallback(() => {
    if (isMinWeek) return;
    const { start, end } = getWeekRange(currentMonday.subtract(7, 'day'));
    onChange(start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD'));
  }, [currentMonday, isMinWeek, onChange]);

  const handleNext = useCallback(() => {
    if (isMaxWeek) return;
    const { start, end } = getWeekRange(currentMonday.add(7, 'day'));
    onChange(start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD'));
  }, [currentMonday, isMaxWeek, onChange]);

  const info = getLotteryInfo(currentMonday);
  const dateRangeLabel = `${currentMonday.format('YYYY-MM-DD')} ~ ${currentSaturday.format('YYYY-MM-DD')}`;

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 3,
        bgcolor: '#ffffff',
        width: 'fit-content',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
        border: '1px solid #f1f5f9',
      }}
    >
      <Typography
        variant="subtitle1"
        fontWeight={600}
        mb={2}
        color="#475569"
        sx={{ fontSize: '0.95rem' }}
      >
        주차 선택
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton
          onClick={handlePrev}
          disabled={isMinWeek}
          size="small"
          sx={{
            width: 32, height: 32,
            border: '1px solid #e2e8f0',
            borderRadius: 2,
            bgcolor: '#f8fafc',
            color: '#475569',
            flexShrink: 0,
            '&:hover': { bgcolor: isMinWeek ? '#f8fafc' : '#eff6ff', borderColor: isMinWeek ? '#e2e8f0' : '#cbd5e1', color: isMinWeek ? '#cbd5e1' : '#0044D2' },
            '&.Mui-disabled': { bgcolor: '#f8fafc', color: '#cbd5e1', border: '1px solid #e2e8f0' },
          }}
        >
          <ChevronLeftIcon sx={{ fontSize: 18 }} />
        </IconButton>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 0.2,
            px: 2,
            py: 1,
            bgcolor: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: 2.5,
            userSelect: 'none',
            minWidth: 200,
          }}
        >
          {info && (
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#1e293b', whiteSpace: 'nowrap' }}>
                {info.weekLabel}
              </Typography>
              <Typography sx={{ fontSize: '0.8rem', color: '#0044D2', fontWeight: 500, whiteSpace: 'nowrap' }}>
                {info.roundLabel}
              </Typography>
            </Box>
          )}
          <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>
            {dateRangeLabel}
          </Typography>
        </Box>

        <IconButton
          onClick={handleNext}
          disabled={isMaxWeek}
          size="small"
          sx={{
            width: 32, height: 32,
            border: '1px solid #e2e8f0',
            borderRadius: 2,
            bgcolor: '#f8fafc',
            color: '#475569',
            flexShrink: 0,
            '&:hover': { bgcolor: isMaxWeek ? '#f8fafc' : '#eff6ff', borderColor: isMaxWeek ? '#e2e8f0' : '#cbd5e1', color: isMaxWeek ? '#cbd5e1' : '#0044D2' },
            '&.Mui-disabled': { bgcolor: '#f8fafc', color: '#cbd5e1', border: '1px solid #e2e8f0' },
          }}
        >
          <ChevronRightIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Box>
  );
}
